"use client"

import type React from "react"
import { createContext, useCallback, useContext, useEffect, useState } from "react"

import { AZURE_AUTH_CHANGED_EVENT } from "@/lib/azure/auth-events"
import type { AuthSession as Session, AuthUser as User } from "@/lib/azure/compat-client"
import type { Company, JobApplicant } from "@/lib/domain"

type UserType = "company" | "job_seeker" | null

interface AuthContextType {
  user: User | null
  company: Company | null
  jobSeeker: JobApplicant | null
  userType: UserType
  isCompany: boolean
  isJobSeeker: boolean
  loading: boolean
  session: Session | null
  initialized: boolean
  signOut: () => Promise<void>
  refreshCompany: () => Promise<void>
  refreshJobSeeker: () => Promise<void>
  refreshSession: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  company: null,
  jobSeeker: null,
  userType: null,
  isCompany: false,
  isJobSeeker: false,
  loading: true,
  session: null,
  initialized: false,
  signOut: async () => {},
  refreshCompany: async () => {},
  refreshJobSeeker: async () => {},
  refreshSession: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [company, setCompany] = useState<Company | null>(null)
  const [jobSeeker, setJobSeeker] = useState<JobApplicant | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [statusResponse, profileResponse] = await Promise.all([
        fetch("/api/system/status", {
          credentials: "same-origin",
          cache: "no-store",
        }),
        fetch("/api/profile", {
          credentials: "same-origin",
          cache: "no-store",
        }),
      ])

      if (statusResponse.status === 401 || profileResponse.status === 401) {
        setUser(null)
        setCompany(null)
        setJobSeeker(null)
        setSession(null)
        return
      }
      if (!statusResponse.ok || !profileResponse.ok) {
        throw new Error("Could not load the current account.")
      }

      const status = await statusResponse.json()
      const profileResult = await profileResponse.json()
      const current = status.user as {
        id: string
        email: string
        name: string
        userType: "company" | "job_seeker"
      } | null

      if (!current) {
        setUser(null)
        setCompany(null)
        setJobSeeker(null)
        setSession(null)
        return
      }

      const currentUser = {
        id: current.id,
        email: current.email,
        user_metadata: {
          user_type: current.userType,
          company_name:
            current.userType === "company"
              ? profileResult.profile?.company_name
              : undefined,
          full_name:
            current.userType === "job_seeker"
              ? profileResult.profile?.full_name
              : undefined,
        },
        app_metadata: { provider: "entra_external_id" },
        aud: "authenticated",
        created_at: profileResult.profile?.created_at ?? new Date(0).toISOString(),
      } as User
      const currentSession = {
        access_token: "",
        refresh_token: "",
        expires_in: 8 * 60 * 60,
        expires_at: Math.floor(Date.now() / 1000) + 8 * 60 * 60,
        token_type: "bearer",
        user: currentUser,
      } as Session

      setUser(currentUser)
      setSession(currentSession)
      if (current.userType === "company") {
        setCompany({
          ...profileResult.profile,
          logo_url: profileResult.profile?.logo_url ?? null,
        } as Company)
        setJobSeeker(null)
      } else {
        setCompany(null)
        setJobSeeker({
          ...profileResult.profile,
          avatar_url: profileResult.profile?.avatar_url ?? null,
        } as JobApplicant)
      }
    } catch (error) {
      console.error("[useAuth] Account initialization failed", error)
      setUser(null)
      setCompany(null)
      setJobSeeker(null)
      setSession(null)
    } finally {
      setInitialized(true)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    const handleAuthChanged = (event: Event) => {
      const operation = load()
      const detail = (event as CustomEvent<{
        waitUntil?: (operation: Promise<void>) => void
      }>).detail
      detail?.waitUntil?.(operation)
    }
    window.addEventListener(AZURE_AUTH_CHANGED_EVENT, handleAuthChanged)
    return () =>
      window.removeEventListener(AZURE_AUTH_CHANGED_EVENT, handleAuthChanged)
  }, [load])

  const signOut = useCallback(async () => {
    window.location.assign("/api/auth/external/logout")
  }, [])
  const refreshCompany = useCallback(async () => load(), [load])
  const refreshJobSeeker = useCallback(async () => load(), [load])
  const refreshSession = useCallback(async () => load(), [load])

  return (
    <AuthContext.Provider
      value={{
        user,
        company,
        jobSeeker,
        userType: company ? "company" : jobSeeker ? "job_seeker" : null,
        isCompany: Boolean(company),
        isJobSeeker: Boolean(jobSeeker),
        loading,
        session,
        initialized,
        signOut,
        refreshCompany,
        refreshJobSeeker,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
